import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Buildings, ChatCircleDots, FileArrowDown, Warning, SealCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { applicationsApi, APPLICATION_STATUS_LABELS, timeAgo } from "@/lib/jobsApi";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/** Single application detail (visible to applicant AND employer). */
export default function ApplicationDetail() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setA(await applicationsApi.detail(appId)); }
    catch { toast.error("Application not found"); navigate("/jobs"); }
    finally { setLoading(false); }
  }, [appId, navigate]);

  useEffect(() => { load(); }, [load]);

  const withdraw = async () => {
    if (!window.confirm("Withdraw this application?")) return;
    try { await applicationsApi.withdraw(appId); toast.success("Withdrawn"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-8 text-white/50">Loading…</div>;
  if (!a) return null;

  const isApplicant = a.applicant_id === user?.user_id;
  const j = a.job || {};

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-4" data-testid="application-page">
      <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white flex items-center gap-1 text-sm">
        <ArrowLeft size={14} weight="bold" /> Back
      </button>

      <div className="flex items-start gap-3">
        {j.employer_company_logo ? (
          <img src={j.employer_company_logo} alt="" className="w-16 h-16 rounded-xl object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-white/5 flex items-center justify-center">
            <Buildings size={28} className="text-white/40" weight="duotone" />
          </div>
        )}
        <div className="flex-1">
          <h1 className="font-heading text-2xl md:text-3xl font-black">{j.title || "Application"}</h1>
          <p className="text-white/60">{j.employer_company_name} · {j.location}</p>
        </div>
        <span className="px-2 py-1 rounded bg-blue-500/15 text-blue-300 text-xs font-bold uppercase">
          {APPLICATION_STATUS_LABELS[a.status] || a.status}
        </span>
      </div>

      <Card className="bg-white/[0.03] border-white/10">
        <CardContent className="p-4 space-y-3">
          <div className="text-xs text-white/50">Applied {timeAgo(a.created_at)} · Updated {timeAgo(a.updated_at)}</div>
          {a.status_note && (
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-sm text-yellow-100">
              <strong>Note from employer:</strong> {a.status_note}
            </div>
          )}
          {a.interview_at && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-100 flex items-center gap-2">
              <SealCheck size={16} weight="fill" className="text-emerald-400" />
              Interview scheduled for {new Date(a.interview_at).toLocaleString()}
            </div>
          )}
          {a.cover_letter && (
            <div>
              <h3 className="text-xs uppercase tracking-widest text-white/50 font-black">Cover letter</h3>
              <p className="text-white/80 mt-1 whitespace-pre-line">{a.cover_letter}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
            {a.cv_url && (
              <a href={a.cv_url} target="_blank" rel="noreferrer" data-testid="app-cv-link" className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-white/15 text-sm hover:bg-white/5">
                <FileArrowDown size={14} weight="bold" /> Download CV
              </a>
            )}
            <Button onClick={() => navigate(`/messages/${appId}`)} data-testid="app-message-btn" variant="outline" className="border-white/20 bg-transparent hover:bg-white/5 gap-1">
              <ChatCircleDots size={14} weight="bold" /> Message
            </Button>
            <Button onClick={() => navigate(`/jobs/${a.job_id}`)} variant="ghost">View job posting</Button>
            {isApplicant && a.status !== "withdrawn" && a.status !== "accepted" && a.status !== "rejected" && (
              <Button onClick={withdraw} data-testid="app-withdraw" variant="ghost" className="text-red-300 hover:text-red-200 hover:bg-red-500/10 gap-1">
                <Warning size={14} weight="bold" /> Withdraw
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
